import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';

interface StoredItem<T> {
  value: T;
  expiresAt?: number;
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private storageServ: Storage | null = null;
  private ready: Promise<void>;

  constructor(private storage: Storage) {
    this.ready = this.init();
  }

  private async init(): Promise<void> {
    try {
      this.storageServ = await this.storage.create();
    } catch (err) {
      throw err;
    }
  }

  public async ensureReady(): Promise<void> {
    if (!this.storageServ) {
      await this.ready;
    }
  }

  public async setInteractionSound(key: string, val: string): Promise<void> {
    await this.ensureReady();
    await this.storageServ!.set(key, val);
  }

  public async getInteractionSound(key: string): Promise<string | null> {
    await this.ensureReady();
    return this.storageServ!.get(key);
  }

  public async isInteractionSoundOn(): Promise<boolean> {
    await this.ensureReady();
    const val = await this.storageServ!.get('interactionSound');
    return val === 'true';
  }

  public async checkForCreatedStorage(): Promise<void> {
    await this.ensureReady();
    const val = await this.getInteractionSound('interactionSound');
    if (val === null || val === undefined) {
      await this.setInteractionSound('interactionSound', 'true');
    }
  }


  public async setItem<T = any>(key: string, value: T, ttlMs?: number): Promise<void> {
    await this.ensureReady();
    try {
      const wrapped: StoredItem<T> = {
        value,
        expiresAt: ttlMs ? Date.now() + ttlMs : undefined
      };
      await this.storageServ!.set(key, wrapped);
    } catch (err) {
      console.error(`Error setting key '${key}' in storage`, err);
    }
  }

  public async getItem<T = any>(key: string): Promise<T | null> {
    await this.ensureReady();
    try {
      const raw = await this.storageServ!.get(key);
      if (raw === null || raw === undefined) return null;

      if (typeof raw === 'object' && raw !== null && 'value' in raw) {
        const stored = raw as StoredItem<T>;
        if (stored.expiresAt && Date.now() > stored.expiresAt) {
          await this.removeItem(key);
          return null;
        }
        return stored.value;
      }

      return raw as T;
    } catch (err) {
      console.warn(`Error reading key '${key}' from storage`, err);
      return null;
    }
  }

  public async removeItem(key: string): Promise<void> {
    await this.ensureReady();
    try {
      await this.storageServ!.remove(key);
    } catch (err) {
      console.error(`Error removing key '${key}'`, err);
    }
  }

  public async clear(): Promise<void> {
    await this.ensureReady();
    try {
      await this.storageServ!.clear();
    } catch (err) {
      console.error('Error clearing storage', err);
    }
  }

  public async keys(): Promise<string[]> {
    await this.ensureReady();
    try {
      return await this.storageServ!.keys();
    } catch (err) {
      console.error('Error getting storage keys', err);
      return [];
    }
  }

  public async dump(): Promise<Record<string, any>> {
    await this.ensureReady();
    const result: Record<string, any> = {};
    const keys = await this.keys();

    for (const key of keys) {
      result[key] = await this.getItem(key);
    }

    return result;
  }
}
